import React, { useMemo, useState } from 'react';

import { getCalendarDayColors } from '../theme';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const MONTH_LABEL = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });

function dateKey(year, month, day) {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, count) {
  return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

/** Map session count (1–10+) to a theme gradient; 10+ uses the darkest shade. */
export function sessionDayColors(count) {
  return getCalendarDayColors(count);
}

/**
 * Month grid where only days that have saved sessions are selectable.
 * `dates` is [{ date: 'YYYY-MM-DD', count }] in the viewer's own time zone.
 */
function SessionCalendar({ dates = [], selectedDate = '', onSelectDate, onClear }) {
  const countsByDate = useMemo(() => {
    const map = new Map();
    for (const item of dates) {
      if (item?.date) map.set(item.date, Number(item.count) || 0);
    }
    return map;
  }, [dates]);

  const [viewMonth, setViewMonth] = useState(() => {
    if (selectedDate) {
      const [year, month] = selectedDate.split('-').map(Number);
      if (year && month) return new Date(year, month - 1, 1);
    }
    const latest = dates.length ? dates[dates.length - 1].date : '';
    if (latest) {
      const [year, month] = latest.split('-').map(Number);
      if (year && month) return new Date(year, month - 1, 1);
    }
    return startOfMonth(new Date());
  });

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const leadingBlanks = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayKey = useMemo(() => {
    const now = new Date();
    return dateKey(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const monthHasSessions = useMemo(() => {
    const prefix = `${year}-${String(month + 1).padStart(2, '0')}`;
    return [...countsByDate.keys()].some((key) => key.startsWith(prefix));
  }, [countsByDate, year, month]);

  const cells = [];
  for (let i = 0; i < leadingBlanks; i += 1) {
    cells.push({ key: `blank-${i}`, blank: true });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const key = dateKey(year, month, day);
    cells.push({
      key,
      day,
      count: countsByDate.get(key) || 0,
      isToday: key === todayKey,
      isSelected: key === selectedDate,
    });
  }

  return (
    <div className="session-calendar">
      <div className="calendar-header">
        <button
          type="button"
          className="calendar-nav"
          onClick={() => setViewMonth((current) => addMonths(current, -1))}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="calendar-month">{MONTH_LABEL.format(viewMonth)}</span>
        <button
          type="button"
          className="calendar-nav"
          onClick={() => setViewMonth((current) => addMonths(current, 1))}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="calendar-weekdays" aria-hidden="true">
        {WEEKDAYS.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>

      <div className="calendar-grid" role="grid" aria-label="Sessions by date">
        {cells.map((cell) =>
          cell.blank ? (
            <span key={cell.key} className="calendar-day empty" />
          ) : (
            <button
              key={cell.key}
              type="button"
              className={[
                'calendar-day',
                cell.count ? 'has-sessions' : 'no-sessions',
                cell.isSelected ? 'selected' : '',
                cell.isToday ? 'today' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={cell.count ? sessionDayColors(cell.count) : undefined}
              disabled={!cell.count}
              aria-disabled={!cell.count}
              aria-pressed={cell.isSelected}
              aria-label={
                cell.count
                  ? `${cell.day}, ${cell.count} session${cell.count === 1 ? '' : 's'}`
                  : `${cell.day}, no sessions`
              }
              title={
                cell.count
                  ? `${cell.count} session${cell.count === 1 ? '' : 's'}`
                  : 'No sessions on this day'
              }
              onClick={() => cell.count && onSelectDate(cell.key)}
            >
              <span className="calendar-day-number">{cell.day}</span>
              {cell.count ? <span className="calendar-day-count">{cell.count}</span> : null}
            </button>
          )
        )}
      </div>

      {!monthHasSessions ? (
        <p className="field-note">No sessions this month. Use ‹ and › to find a month with sessions.</p>
      ) : (
        <p className="field-note">
          Darker green means more sessions (1 light → 10+ darkest). Empty days cannot be selected.
        </p>
      )}

      {selectedDate ? (
        <button type="button" className="secondary" onClick={onClear}>
          Clear date filter
        </button>
      ) : null}
    </div>
  );
}

export default SessionCalendar;
